"use client";

/**
 * Shared loading spinner.
 *
 * Defaults match the pre-existing inline spinners used on run/pod detail pages
 * and the production-runs list. Use `size="sm"` for inline/button contexts.
 */
export type LoadingSpinnerProps = {
  /** Named vertical padding preset for the wrapper. Set to `"none"` to render the bare spinner. */
  pad?: "none" | "sm" | "md" | "lg";
  /** "lg" = h-8 w-8 border-4 (default), "sm" = h-4 w-4 border-2. */
  size?: "lg" | "sm";
  /** Optional accessible label. Defaults to "Loading". */
  label?: string;
  /** Extra classes for the wrapper. */
  className?: string;
};

const PAD_CLS: Record<NonNullable<LoadingSpinnerProps["pad"]>, string> = {
  none: "",
  sm: "py-8",
  md: "py-16",
  lg: "py-20",
};

export default function LoadingSpinner({
  pad = "lg",
  size = "lg",
  label = "Loading",
  className,
}: LoadingSpinnerProps) {
  const sizeCls =
    size === "sm"
      ? "h-4 w-4 border-2 border-zinc-200 border-t-blue-600"
      : "h-8 w-8 border-4 border-zinc-200 border-t-blue-600";

  const spinner = (
    <div
      role="status"
      aria-label={label}
      className={`animate-spin rounded-full ${sizeCls}`}
    />
  );

  if (pad === "none") return spinner;

  return (
    <div
      className={`flex items-center justify-center ${PAD_CLS[pad]} ${className ?? ""}`.trim()}
    >
      {spinner}
    </div>
  );
}
